import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaCameraRetro // <-- Ye line add kar di hai
} from 'react-icons/fa';
import './SpitiAdventure.css';

const SpitiAdventure = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Chandigarh to Shimla",
      points: [
        "Arrival in Chandigarh, where the journey begins.",
        "Travel towards Shimla, enjoying the scenic drive through the Himalayan foothills.",
        "Upon arrival, check in to the hotel and relax.",
        "Dinner and overnight stay in Shimla."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Shimla to Sangla / Rakcham",
      points: [
        "After breakfast, depart from Shimla towards Sangla Valley or Rakcham in the Kinnaur region.",
        "The drive offers stunning views of rivers, mountains, and valleys.",
        "Upon arrival, check in to the hotel.",
        "Dinner and overnight stay in Sangla / Rakcham."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 3,
      title: "Sangla / Rakcham to Tabo (via Khab Sangam & Nako Lake)",
      points: [
        "Continue the journey deeper into Spiti Valley after breakfast.",
        "Visit Khab Sangam – the confluence of the Spiti and Sutlej Rivers.",
        "Explore Nako Lake – a beautiful high-altitude lake surrounded by mountains.",
        "Arrival at Tabo and hotel check-in.",
        "Dinner and overnight stay in Tabo."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 4,
      title: "Tabo to Kaza (via Dhankar Monastery & Pin Valley)",
      points: [
        "Travel towards Kaza, the main town of Spiti Valley.",
        "En route visit the historic Dhankar Monastery perched on a cliff.",
        "Explore Pin Valley National Park for its unique landscape.",
        "Arrival in Kaza and hotel check-in.",
        "Dinner and overnight stay in Kaza."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 5,
      title: "Kaza Sightseeing (High Altitude Villages)",
      points: [
        "Explore Langza Village – known for the giant Buddha statue and ancient fossils.",
        "Visit Hikkim – home to the world’s highest post office.",
        "Visit Komic – one of the highest motorable villages in the world.",
        "Visit Key Monastery – the largest and most iconic monastery in Spiti.",
        "Experience the spectacular Chicham Bridge – one of Asia’s highest suspension bridges.",
        "Return to Kaza for dinner and overnight stay."
      ],
      icon: <FaCameraRetro />
    },
    {
      day: 6,
      title: "Kaza to Kalpa (via Gue Mummy)",
      points: [
        "Depart for Kalpa after breakfast.",
        "En route visit Gue Village – famous for the 500-year-old Gue Mummy Temple.",
        "Arrival in Kalpa and check-in to the hotel.",
        "Enjoy breathtaking panoramic views of the Kinnaur Kailash mountain range.",
        "Dinner and overnight stay in Kalpa."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 7,
      title: "Kalpa to Chandigarh",
      points: [
        "After breakfast, check out and begin the return journey towards Chandigarh.",
        "Enjoy the final leg of the scenic mountain journey through the HP hills.",
        "Arrival in Chandigarh by evening.",
        "Tour concludes with wonderful memories of the Spiti Valley Himalayan Adventure."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Spiti Valley Himalayan Adventure – <br /> Shimla, Sangla, Tabo, Kaza & Kalpa</h1>
          <p className="trip-duration">Duration: 6 Nights & 7 Days of Extraordinary Exploration</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Spiti, Kinnaur, Shimla</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Adventure & Cultural Tour</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>High Altitude Expedition</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1539267691846-3364593f08d0?q=80&w=1472&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Key Monastery" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1684823930343-26a531403928?q=80&w=475&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Spiti Landscape" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1737121469091-97cc0d6fe33a?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Chicham Bridge" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1543813903-b5c6dd4a0ad4?q=80&w=1031&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Langza Buddha" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Scenic Himalayan road journey through Spiti Valley</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the beautiful Sangla and Rakcham valleys</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore ancient Tabo Monastery (Ajanta of the Himalayas)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Dhankar Monastery and Pin Valley National Park</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore Langza, Hikkim, and Komic high-altitude villages</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Key Monastery and Chicham Bridge (Asia's highest)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the mysterious 500-year-old Gue Mummy Monastery</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Enjoy panoramic views of the Kinnaur Kailash range from Kalpa</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in comfortable hotels (6 Nights stay as per itinerary)</li>
              <li><FaClock /> 1N Shimla | 1N Sangla | 1N Tabo | 2N Kaza | 1N Kalpa</li>
              <li><FaStar /> Accommodation on double sharing basis</li>
              <li><FaUtensils /> Meal Plan: MAP (6 Breakfasts & 6 Dinners at hotels)</li>
              <li><FaMapMarkerAlt /> All sightseeing and transfers as per itinerary</li>
              <li><FaBus /> Transportation by Tempo Traveller (17 Seater) - 2 vehicles for tour</li>
              <li><FaPaperPlane /> Pick-up and drop from Chandigarh</li>
              <li><FaCheckCircle /> Driver charges, fuel, toll, parking, and permits</li>
              <li><FaShieldAlt /> Tour coordination and assistance throughout the journey</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets for arrival and departure</li>
              <li><FaUtensils /> Lunch or any meals not mentioned in inclusions</li>
              <li><FaTimesCircle /> Room heater charges in hotels (if required)</li>
              <li><FaTimesCircle /> Entry fees for monasteries, monuments, or sightseeing places</li>
              <li><FaTimesCircle /> Camera or video camera charges where applicable</li>
              <li><FaTimesCircle /> Personal expenses (Tips, laundry, shopping, etc.)</li>
              <li><FaShieldAlt /> Travel insurance or medical expenses</li>
              <li><FaTimesCircle /> Additional costs due to road conditions, weather, or landslides</li>
              <li><FaCheckCircle /> Any service not mentioned in the inclusions section</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">About This Journey</h2>
          <p className="about-text">
            The Spiti Valley Himalayan Adventure is an unforgettable journey through one of the most spectacular and remote regions of the Indian Himalayas. Starting from Chandigarh, the journey passes through charming Himalayan towns like Shimla, Sangla, Rakcham, Tabo, Kaza, and Kalpa. 
            <br /><br />
            Travelers will experience stunning natural beauty, visit ancient Buddhist monasteries, explore unique villages like Langza, Hikkim, and Komic, and witness the majestic Chicham Bridge and Khab Sangam. This adventure offers a perfect combination of nature, culture, spirituality, and Himalayan exploration, making it an ideal experience for travelers seeking something truly extraordinary.
          </p>
        </motion.section>

        {/* TERMS & PRIVACY */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* TRAVEL INFO & NOTES SECTION */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth and comfortable journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default SpitiAdventure;